import { acceptForeignEvent, assertValidEvent } from '$domain/events';
import type { DomainEvent, EventType } from '$domain/events';
import { openComebackDb } from '$infra/db';
import type { AppendManyResult, EventStore } from '$infra/event-store';

function isConstraintError(error: unknown): boolean {
  return typeof error === 'object'
    && error !== null
    && 'name' in error
    && error.name === 'ConstraintError';
}

export function createIdbEventStore(): EventStore {
  return {
    async append(event: DomainEvent): Promise<void> {
      const validEvent = assertValidEvent(event);
      const database = await openComebackDb();

      try {
        // `add`, rather than `put`, preserves event-log immutability.
        await database.add('events', validEvent);
      } catch (error) {
        if (isConstraintError(error)) {
          throw new Error(`An event with id ${validEvent.id} already exists`);
        }
        throw error;
      }
    },

    async appendAll(events: DomainEvent[]): Promise<void> {
      if (events.length === 0) return;
      const valid = events.map((event) => assertValidEvent(event));
      const database = await openComebackDb();

      // One transaction for the whole batch. IndexedDB aborts it on any error,
      // so a failure leaves the log exactly as it was rather than half-written.
      const tx = database.transaction('events', 'readwrite');
      try {
        // `tx.done` goes into the same Promise.all as the adds. Awaiting it
        // separately leaves its abort rejection unobserved for a tick, which
        // Node reports as an unhandled rejection and which would take a real
        // browser's error reporting with it.
        await Promise.all([...valid.map((event) => tx.store.add(event)), tx.done]);
      } catch (error) {
        if (isConstraintError(error)) {
          throw new Error('Refusing to append a batch containing an event id that already exists');
        }
        throw error;
      }
    },

    async appendMany(events: DomainEvent[]): Promise<AppendManyResult> {
      const database = await openComebackDb();
      const result: AppendManyResult = { added: 0, skipped: 0, dropped: 0 };

      for (const rawEvent of events) {
        const outcome = acceptForeignEvent(rawEvent);
        if (!outcome.ok && outcome.reason === 'malformed') {
          result.dropped += 1;
          continue;
        }

        // Unknown and future-version events are deliberately retained. A newer
        // device may understand them even though this client cannot reduce them.
        try {
          await database.add('events', outcome.event);
          result.added += 1;
        } catch (error) {
          if (isConstraintError(error)) {
            result.skipped += 1;
            continue;
          }
          throw error;
        }
      }

      return result;
    },

    async getAll(): Promise<DomainEvent[]> {
      const database = await openComebackDb();
      // IndexedDB iterates an object store by primary key, and ULIDs sort by time.
      return database.getAll('events');
    },

    async getSince(id: string | null): Promise<DomainEvent[]> {
      const database = await openComebackDb();
      return id === null
        ? database.getAll('events')
        : database.getAll('events', IDBKeyRange.lowerBound(id, true));
    },

    async getByType(type: EventType): Promise<DomainEvent[]> {
      const database = await openComebackDb();
      return database.getAllFromIndex('events', 'type', type);
    },

    async lastId(): Promise<string | null> {
      const database = await openComebackDb();
      const cursor = await database
        .transaction('events')
        .store.openKeyCursor(null, 'prev');
      return cursor?.key ?? null;
    },

    async count(): Promise<number> {
      const database = await openComebackDb();
      return database.count('events');
    },

    async exportAll(): Promise<DomainEvent[]> {
      const database = await openComebackDb();
      return database.getAll('events');
    },

    async wipe(): Promise<void> {
      const database = await openComebackDb();
      // Wiping the log is a storage operation; scratch remains harmlessly intact.
      await database.clear('events');
    },
  };
}
